import {range} from 'lodash';
import {DateTime, Interval} from 'luxon';

import {collectEnumValues} from '../../libs/shared/helpers/src/typescript/enumHelpers';
import {PaletteColorsEnum} from '../../libs/shared/ui/src/styles/paletteModel';
import {CalendarWeekDaysEnum, CalendarWeekDaysShortEnum} from '../models/api/calendarCommonTypes';
import {CalendarEventVersionStartEnd} from '../models/api/calendarEventVersionModel';
import {CalendarVersionModel} from '../models/api/calendarVersionModel';
import {TimeIntervalModel} from '../models/api/meetingRoomModel';

export interface StartEndData {
  startDateTime: DateTime;
  endDateTime: DateTime;
  isAllDay: boolean;
}

export function isStartEndDataEqual(s1: StartEndData, s2: StartEndData) {
  return (
    s1.startDateTime.equals(s2.startDateTime) &&
    s1.endDateTime.equals(s2.endDateTime) &&
    s1.isAllDay === s2.isAllDay
  );
}

/** Get the sorted weekdays number, starting with the provided first day of week. */
export function getSortedWeekdays(calendarWeekStartDay: CalendarWeekDaysEnum) {
  const sortedCalendarWeekdays = getSortedWeekdayNames(calendarWeekStartDay);
  return sortedCalendarWeekdays.map((weekdayName) => calendarWeekdayToWeekdayNumber[weekdayName]);
}

export function getSortedWeekdayNames(calendarWeekStartDay: CalendarWeekDaysEnum) {
  const unorderedWeekdays = collectEnumValues(CalendarWeekDaysEnum);

  const splitIndex = unorderedWeekdays.indexOf(calendarWeekStartDay);
  const left = unorderedWeekdays.slice(0, splitIndex);
  const right = unorderedWeekdays.slice(splitIndex);
  return [...right, ...left];
}

export const calendarWeekdayToWeekdayNumber = {
  [CalendarWeekDaysEnum.MONDAY]: 1,
  [CalendarWeekDaysEnum.TUESDAY]: 2,
  [CalendarWeekDaysEnum.WEDNESDAY]: 3,
  [CalendarWeekDaysEnum.THURSDAY]: 4,
  [CalendarWeekDaysEnum.FRIDAY]: 5,
  [CalendarWeekDaysEnum.SATURDAY]: 6,
  [CalendarWeekDaysEnum.SUNDAY]: 7,
};

export const weekdayNumberToCalendarWeekdayMap: {[_: string]: CalendarWeekDaysEnum} = {
  1: CalendarWeekDaysEnum.MONDAY,
  2: CalendarWeekDaysEnum.TUESDAY,
  3: CalendarWeekDaysEnum.WEDNESDAY,
  4: CalendarWeekDaysEnum.THURSDAY,
  5: CalendarWeekDaysEnum.FRIDAY,
  6: CalendarWeekDaysEnum.SATURDAY,
  7: CalendarWeekDaysEnum.SUNDAY,
};

export const CalendarWeekdayToShortName: Record<CalendarWeekDaysEnum, CalendarWeekDaysShortEnum> = {
  [CalendarWeekDaysEnum.SUNDAY]: CalendarWeekDaysShortEnum.SUNDAY,
  [CalendarWeekDaysEnum.MONDAY]: CalendarWeekDaysShortEnum.MONDAY,
  [CalendarWeekDaysEnum.TUESDAY]: CalendarWeekDaysShortEnum.TUESDAY,
  [CalendarWeekDaysEnum.WEDNESDAY]: CalendarWeekDaysShortEnum.WEDNESDAY,
  [CalendarWeekDaysEnum.THURSDAY]: CalendarWeekDaysShortEnum.THURSDAY,
  [CalendarWeekDaysEnum.FRIDAY]: CalendarWeekDaysShortEnum.FRIDAY,
  [CalendarWeekDaysEnum.SATURDAY]: CalendarWeekDaysShortEnum.SATURDAY,
};

/** Get the full list of days for the provided month, sorted by the provided week days. */
export function getCalendarDays(focusedMonth: DateTime, weekdays: ReadonlyArray<number>) {
  // Find the first datetime of the months we're going to work with.
  const previousMonth = focusedMonth.minus({months: 1});
  const nextMonth = focusedMonth.plus({months: 1});

  // Figure out the size of the left & right padding.
  const startOfFocusedMonth = focusedMonth.startOf('month');
  const previousPad = weekdays.indexOf(startOfFocusedMonth.weekday);
  const rows = Math.ceil((focusedMonth.daysInMonth + previousPad) / 7);
  const totalBoxes = rows * 7;
  const nextPad = totalBoxes - (previousPad + focusedMonth.daysInMonth);

  // Build the day numbers for each range.
  const previousMonthDayNumbers = range(
    previousMonth.daysInMonth - previousPad + 1,
    previousMonth.daysInMonth + 1,
  );
  const currentMonthDayNumbers = buildFilledArray(focusedMonth.daysInMonth);
  const nextMonthDayNumbers = buildFilledArray(nextPad);

  // Map the day numbers to datetimes.
  const previousMonthDays = previousMonthDayNumbers.map((day) => previousMonth.set({day}));
  const currentMonthDays = currentMonthDayNumbers.map((day) => focusedMonth.set({day}));
  const nextMonthDays = nextMonthDayNumbers.map((day) => nextMonth.set({day}));

  // Combine everything and return.
  return [...previousMonthDays, ...currentMonthDays, ...nextMonthDays];
}

function buildFilledArray(length: number) {
  return range(1, length + 1);
}

// Return true if any of the time ranges overlap with the startEndData.
export function checkTimeIntervalOverlap(
  startEndData: StartEndData,
  timeIntervals: ReadonlyArray<TimeIntervalModel>,
) {
  const {startDateTime, endDateTime, isAllDay} = startEndData;

  const eventInterval = Interval.fromDateTimes(
    isAllDay ? startDateTime.startOf('day') : startDateTime,
    isAllDay ? endDateTime.startOf('day') : endDateTime,
  );

  const overlappingInterval = timeIntervals.find((i) => {
    const interval = Interval.fromDateTimes(DateTime.fromMillis(i.startsAt), DateTime.fromMillis(i.endsAt));
    return interval.overlaps(eventInterval);
  });

  return Boolean(overlappingInterval);
}

export function buildCalendarEventVersionStartEnd(startEndData: StartEndData): CalendarEventVersionStartEnd {
  const {startDateTime, endDateTime, isAllDay} = startEndData;
  const timezone = startDateTime.zoneName;

  if (isAllDay) {
    return {
      isAllDay: true,
      start: {
        date: startDateTime.toISODate(),
        timezone,
      },
      end: {
        date: endDateTime.toISODate(),
        timezone: undefined,
      },
    };
  }

  return {
    isAllDay: false,
    start: {
      dateTime: startDateTime.toISO(),
      timezone,
    },
    end: {
      dateTime: endDateTime.toISO(),
      timezone: undefined,
    },
  };
}

// The color can be set to three, distinct, values:
// 1. `undefined`: NOP.
// 2. `null`: Default color (the calendar event version inherits it's color from the calendar version).
// 3. `<color_value>`: color value to be set
export function buildCalendarEventVersionColor(
  color: PaletteColorsEnum,
  colorHasBeenEdited: boolean,
  selectedCalendarData: {
    calendarVersion: CalendarVersionModel;
  },
): {
  color: PaletteColorsEnum | null;
  colorIsNop: boolean;
} {
  const defaultColor = selectedCalendarData.calendarVersion.color;

  // NOP, because the user did not indicate that they'd like to change to the color.
  //
  // We only NOP in cases where the user did not interact with the CEV color picker at all, if they
  // select a color (even if it's the same color as it was prior to the edit) we honor their choice.
  // However we NOP if the user doesn't interact with the color picker at all, so we don't potentially
  // overwrite a more recent color value on the server with stale client data.
  if (!colorHasBeenEdited) {
    return {
      color,
      colorIsNop: true,
    };
  }

  // Set the color so that it uses the "default" calendar version color.
  if (color === defaultColor) {
    return {
      color: null,
      colorIsNop: false,
    };
  }

  // Set the color to a non-default color value, e.g., red.
  return {
    color,
    colorIsNop: false,
  };
}

export const fullCalendarArbitraryEventIdPrefix = 'fullcalendar_arbitrary_event';
export const buildFullCalendarArbitraryEventId = (arbitraryCalendarEventId: string) =>
  `${fullCalendarArbitraryEventIdPrefix}-${arbitraryCalendarEventId}`;

export const sortedCalendarColors = [
  PaletteColorsEnum.PURPLE,
  PaletteColorsEnum.GREEN,
  PaletteColorsEnum.ORANGE,
  PaletteColorsEnum.YELLOW,
  PaletteColorsEnum.PINK,
  PaletteColorsEnum.RED,
  PaletteColorsEnum.TEAL,
  PaletteColorsEnum.BLUE,
  PaletteColorsEnum.GREY,
];
